import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { UiPanelComponent } from '@template-ui/components';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  completionProviders,
  WorkflowCompletionRequirement,
  WorkflowRequirementProvider,
} from '@app/shared/workflow-commands/workflow-completion-requirements';
import {
  WorkflowDefinitionDocument,
  WorkflowProcessDataField,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import {
  installedReference,
  sameCapabilityReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import {
  addStepCommand,
  commandIdentity,
  initializeBlankStep,
  StepAuthoringCatalog,
  StepAuthoringCommand,
  StepCommand,
} from './workflow-step-composition';
import { isUnsupportedCaptureForm } from './workflow-capture-form-validation';
import { applyRequirementDetailDefaults } from './workflow-requirement-detail-defaults';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import { WorkflowLibrarySelectorComponent } from './workflow-library-selector.component';
import { WorkflowLibraryCategory, WorkflowLibraryClosedReason, WorkflowLibraryEntry } from './workflow-library-selector.types';
import { NoticePolicyEditorComponent } from '@app/phms/workflow-blocks/notice-policy-editor.component';
import {
  defaultNoticePolicy,
  noticePolicyDescription,
  noticeRequirementIssue,
} from '@app/phms/workflow-blocks/notice-policy';
import {
  domainBlockCodes,
  domainBlockManifests,
  noticeBlockCodes,
  sdmfCommandCodes,
} from '@app/phms/workflow-blocks/domain-block-manifests';
import { ensureNoticeGroup } from './workflow-domain-block-authoring';
import { WorkflowVotingConfigurationComponent } from './workflow-voting-configuration.component';
import {
  providesVotingBlock,
  isHearingPanelPopulation,
  isCombinedVotingRound,
} from '@app/shared/workflow-voting/voting-sources';
import {
  isVotingRequirement,
  VOTING_POPULATION,
  VOTING_ROUND,
} from '@app/shared/workflow-voting/voting.models';
import {
  createNoticeHearingField,
  noticeHearingFieldIssue,
  resolveNoticeSource,
} from './workflow-notice-source';

@Component({
  selector: 'phms-workflow-step-requirements-editor',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    UiPanelComponent,
    WorkflowLibrarySelectorComponent,
    NoticePolicyEditorComponent,
    WorkflowVotingConfigurationComponent,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './workflow-step-requirements-editor.component.html',
  styleUrl: './workflow-step-requirements-editor.component.scss',
})
export class WorkflowStepRequirementsEditorComponent
  implements OnChanges, OnDestroy
{
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() nodeId: string | null = null;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Input() active = true;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() overview = false;
  @Input() overviewNavigationBlocked = false;
  @Input() resetVersion = 0;
  @Input() componentCatalog: StepAuthoringCatalog | null | undefined;
  @Input() componentCapabilities: WorkflowCapabilityCatalogEntry[] = [];
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() editForm = new EventEmitter<StepCommand>();
  @Output() openActions = new EventEmitter<StepCommand | undefined>();
  @Output() manageRequested = new EventEmitter<void>();
  @Output() openStaffPage = new EventEmitter<void>();
  private readonly service = inject(PlatformWorkflowDefinitionLifecycleService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private focusRequest = 0;
  private returnFocus: HTMLElement | null = null;
  catalog: StepAuthoringCatalog | null = null;
  capabilities: WorkflowCapabilityCatalogEntry[] = [];
  loading = false;
  error = '';
  catalogError = '';
  adding = false;
  draft: WorkflowCompletionRequirement | null = null;
  original = '';
  originalId: string | null = null;
  private context = '';
  private catalogContext = '';
  private request = 0;
  private destroyed = false;
  private inputOwner = '';
  private inputReference = '';
  private selectionKey: string | null = null;
  selectedRequirementId: string | null = null;
  private pickerSnapshot = '';
  private pickerFocusRelease: (() => void) | null = null;
  private pickerDestination: 'detail' | 'staff' | null = null;
  private dirty = false;
  private draftReference = '';
  readonly providers = completionProviders;
  readonly libraryCategories: readonly WorkflowLibraryCategory[] = [
    { id: 'forms', label: 'Forms & files' }, { id: 'decisions', label: 'Decisions & voting' }, { id: 'hearing', label: 'Hearing work' },
  ];
  noticeSourceField = '';
  pendingNoticeReference: WorkflowProcessDataField | null = null;
  noticeReferenceError = '';
  @ViewChild(WorkflowVotingConfigurationComponent)
  votingEditor?: WorkflowVotingConfigurationComponent;
  isVoting(row = this.draft): boolean {
    return !!row && isVotingRequirement(row.provider, row.version);
  }
  get votingCode(): string {
    return this.draft?.provider === VOTING_POPULATION
      ? 'voting.population'
      : 'voting.result';
  }
  get noticeSourceResolution() {
    return this.document && this.nodeId
      ? resolveNoticeSource(
          this.document,
          this.nodeId,
          this.noticeSourceField,
          this.pendingNoticeReference ?? undefined,
        )
      : {
          kind: 'blocked' as const,
          message: 'Choose one work step before configuring notice work.',
        };
  }
  get noticeSourceChoices() {
    const source =
      this.document && this.nodeId
        ? resolveNoticeSource(
            this.document,
            this.nodeId,
            undefined,
            this.pendingNoticeReference ?? undefined,
          )
        : null;
    return source?.kind === 'choice' ? source.options : [];
  }
  private prepareNoticeHearing(): void {
    if (!this.document || this.noticeSourceResolution.kind !== 'missing')
      return;
    try {
      this.pendingNoticeReference = createNoticeHearingField(this.document);
      this.noticeSourceField = this.pendingNoticeReference.key;
      this.noticeReferenceError = '';
    } catch (error) {
      this.noticeReferenceError =
        error instanceof Error
          ? error.message
          : 'The workflow’s hearing connection could not be configured.';
    }
  }
  setNoticePolicy(value: string) {
    if (this.draft && !this.disabled) {
      this.draft.settings['policyJson'] = value;
      this.changed();
    }
  }

  get node() {
    const found =
      this.document?.nodes.filter(
        (n) => n.id === this.nodeId && n.typeCode === 'human_step',
      ) ?? [];
    return found.length === 1 ? found[0] : null;
  }
  get commands(): StepCommand[] {
    return this.node?.declaration?.behavior.commands ?? [];
  }
  get rows(): WorkflowCompletionRequirement[] {
    return this.node?.declaration?.behavior.completionRequirements ?? [];
  }
  get selectedRow(): WorkflowCompletionRequirement | null {
    const rows = this.rows.filter(row => !!this.selectedRequirementId && row?.id === this.selectedRequirementId);
    return rows.length === 1 ? rows[0] : null;
  }
  get detailRequirement(): WorkflowCompletionRequirement | null { return this.draft ?? this.selectedRow; }
  get canOpenForm(): boolean {
    const row = this.detailRequirement;
    return row?.provider === completionProviders.form && this.formSources.filter(command => command.captureForm?.id === row.settings['formId']).length === 1;
  }
  get authoringIssue(): string {
    return !this.knownFormat() ? 'This saved workflow format is unsupported. Requirement settings are retained read-only.'
      : !this.canAuthor ? 'You can inspect these requirements. Author permission is required to change them.' : '';
  }
  requirementTypeLabel(row: WorkflowCompletionRequirement): string {
    const providers = this.availableProviders.filter(provider => provider.provider === row.provider && provider.version === row.version &&
      provider.settingsVersion === row.settingsVersion && provider.settingsHash === row.settingsHash);
    return providers.length === 1 ? providers[0].label : `${row?.provider || 'Saved requirement'} v${row?.version ?? '?'}`;
  }
  get libraryEntries(): readonly WorkflowLibraryEntry[] {
    return this.availableProviders.map(provider => {
      const issue = this.providerIssue(provider);
      return { id: this.providerIdentity(provider), label: provider.label, description: provider.description,
        categoryId: [completionProviders.form, completionProviders.document].includes(provider.provider as typeof completionProviders.form)
          ? 'forms' : [completionProviders.decision, completionProviders.quorum, VOTING_POPULATION, VOTING_ROUND].includes(provider.provider as typeof completionProviders.decision) ? 'decisions' : 'hearing',
        icon: `pi ${this.typeIcon(provider.provider)}`, disabledReason: issue || undefined,
        ownerAction: provider.provider === completionProviders.sdmf && !!issue
          ? { id: 'staff-page', label: 'Open Staff page' } : undefined };
    });
  }
  /** Inactive rendering keeps the display token; active rendering and gestures stay fresh. */
  libraryRenderContext(): string {
    return this.active ? this.libraryContext() : this.pickerSnapshot;
  }
  libraryContext(): string {
    const document = this.currentDocument();
    return JSON.stringify([this.ownerKey(), this.documentReference(document), document?.payloadSchemaVersion,
      document?.nodes, document?.edges, document?.subjectTypeCode, document?.processData, document?.capabilityReferences,
      this.catalog, this.capabilities, this.canAuthor, this.interactionBlocked, this.loading, this.catalogError, this.active, this.overview]);
  }
  private providerIdentity(provider: WorkflowRequirementProvider): string {
    return JSON.stringify([provider.provider, provider.version, provider.settingsVersion, provider.settingsHash]);
  }
  private providerIssue(provider: WorkflowRequirementProvider): string {
    const row: WorkflowCompletionRequirement = { id: '', provider: provider.provider, version: provider.version,
      settingsVersion: provider.settingsVersion, settingsHash: provider.settingsHash, settings: {} };
    if (provider.provider === completionProviders.sdmf) {
      const blocks = this.node?.declaration?.detail.blocks.filter(block => block.provider === 'sdmf' && block.code === 'sdmf.analysis' && block.version === provider.version) ?? [];
      row.settings = { ...(blocks.length === 1 ? blocks[0].settings : {}), completionPolicy: 'sdmf.completion.v1' };
      return this.sdmfIssue(row);
    }
    if ([completionProviders.schedulingRequested, completionProviders.hearingScheduled, completionProviders.hearingDecision,
      completionProviders.hearingConduct, completionProviders.hearingGrantReview].includes(provider.provider as typeof completionProviders.hearingDecision)) {
      if (provider.provider === completionProviders.hearingGrantReview)
        row.settings = { ...this.node?.declaration?.detail.blocks.find(block => block.provider === 'hearing' && block.code === 'hearing.grant-review')?.settings };
      return this.validation(row);
    }
    if (provider.provider === completionProviders.notices) {
      const source = this.noticeSourceResolution;
      return source.kind === 'blocked' ? source.message : '';
    }
    if (provider.provider === completionProviders.form && !this.formSources.length && !this.canAddForm)
      return 'Configure one compatible save-only form before using this requirement.';
    if (provider.provider === completionProviders.decision && !this.decisionSources.length && !this.canAddDecision)
      return 'Configure one compatible save-only decision action before using this requirement.';
    return '';
  }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private knownFormat(document = this.currentDocument()): boolean { return !!document && /^1\.(?:[0-9]|1[0-9]|2[0-2])$/.test(document.payloadSchemaVersion); }
  private ownerKey(): string { return JSON.stringify([this.ownerContext?.()]); }
  private adoptLatestForGesture(): boolean {
    const document = this.currentDocument();
    if (!document || this.destroyed || (this.ownerContext && this.ownerKey() !== this.inputOwner) || this.documentReference(document) !== this.inputReference) {
      this.error = 'The selected workflow or step changed. Your input is retained; reopen this owner in its current context.';
      return false;
    }
    this.document = document;
    return true;
  }
  private rememberSelection(): void {
    const row = this.selectedRow;
    if (this.selectionKey && row) this.ownerViews?.remember(this.selectionKey, { id: row.id, provider: row.provider,
      version: row.version, settingsVersion: row.settingsVersion, settingsHash: row.settingsHash });
  }
  private reconcileSelection(): void {
    if (this.dirty || this.adding) return;
    if (!this.selectedRow) this.selectedRequirementId = this.rows.find(row => !!row?.id && this.rows.filter(other => other?.id === row.id).length === 1)?.id ?? null;
    if (!this.active || this.overview) return;
    const row = this.selectedRow;
    if (!row || !this.canAuthor || this.loading || !this.supported(row)) { this.draft = null; return; }
    this.original = this.fingerprint(row.provider); this.draftReference = this.documentReference(this.document);
    this.originalId = row.id; this.draft = structuredClone(row);
    this.rememberSelection();
  }
  get availableProviders(): WorkflowRequirementProvider[] {
    const sdmfBlocks =
      this.node?.declaration?.detail.blocks.filter(
        (block) => block.provider === 'sdmf' && block.code === 'sdmf.analysis',
      ) ?? [];
    const sdmfVersion =
      sdmfBlocks.length === 1
        ? sdmfBlocks[0].version
        : Math.max(
            ...(this.catalog?.requirementProviders ?? [])
              .filter(
                (p) =>
                  p.provider === completionProviders.sdmf &&
                  (p.version === 1 || p.version === 2),
              )
              .map((p) => p.version),
          );
    return (this.catalog?.requirementProviders ?? []).filter(
      (p) =>
        Object.values(completionProviders).includes(
          p.provider as typeof completionProviders.form,
        ) &&
        ((p.provider === completionProviders.sdmf &&
          (p.version === 1 || p.version === 2) &&
          p.settingsVersion === p.version &&
          p.sourceType ===
            (p.version === 1
              ? 'sdmf-certified-visit'
              : 'sdmf-certified-visit-source-two')) ||
          (p.provider !== completionProviders.sdmf &&
            (p.version === 1 ||
              ([
                completionProviders.quorum,
                completionProviders.hearingGrantReview,
              ].includes(p.provider as typeof completionProviders.quorum) &&
                p.version === 2 &&
                p.sourceType ===
                  (p.provider === completionProviders.quorum
                    ? 'voting-round'
                    : 'hearing-grant-review'))) &&
            p.settingsVersion === 1)) &&
        !!p.settingsHash &&
        (p.provider !== completionProviders.sdmf ||
          p.version === sdmfVersion) &&
        (p.provider !== completionProviders.hearingGrantReview ||
          p.version ===
            (this.node?.declaration?.detail.blocks.find(
              (b) =>
                b.provider === 'hearing' && b.code === 'hearing.grant-review',
            )?.version ?? 2)) &&
        (p.provider !== completionProviders.quorum ||
          p.version === 2 ||
          this.quorumMetadataSupported(p)) &&
        this.catalog!.requirementProviders!.filter(
          (other) =>
            other.provider === p.provider && other.version === p.version,
        ).length === 1,
    );
  }
  get disabled(): boolean {
    return (
      !this.canAuthor || this.interactionBlocked || this.loading || !!this.catalogError || !this.node || !this.knownFormat()
    );
  }
  get completionAvailable(): boolean {
    return (
      this.installedCommand('workflow.step.complete')
        ?.supportsCompletionRequirements === true
    );
  }
  get ambiguous(): boolean {
    return (
      this.rows.some((r) => !r?.id) ||
      new Set(this.commands.map(commandIdentity)).size !==
        this.commands.length ||
      new Set(this.rows.map((r) => r?.id)).size !== this.rows.length
    );
  }
  get legacyCompletingActions(): boolean {
    return this.commands.some(
      (c) =>
        c.capability.key.code !== 'workflow.step.complete' &&
        this.contract(c)?.completesStep !== false,
    );
  }
  get formSources(): StepCommand[] {
    return this.commands.filter(
      (c) =>
        c.capability.key.code === 'workflow.form.save' &&
        !!c.captureForm &&
        !isUnsupportedCaptureForm(c.captureForm) &&
        !!this.contract(c),
    );
  }
  get decisionSources(): StepCommand[] {
    return this.commands.filter((c) => {
      const contract = this.contract(c);
      return (
        !!contract?.decisionEvidence &&
        contract.completesStep === false &&
        (contract.decisionEvidence.requiredCommands ?? []).every(
          (ref) =>
            this.commands.filter(
              (other) =>
                sameCapabilityReference(other.capability, ref) &&
                !!this.contract(other),
            ).length === 1,
        )
      );
    });
  }
  get sourceDecision(): StepCommand | undefined {
    const ref = this.draft?.settings['sourceCommand'] as
      | StepCommand['capability']
      | undefined;
    return ref?.key
      ? this.decisionSources.find((c) =>
          sameCapabilityReference(c.capability, ref),
        )
      : undefined;
  }
  get outcomeChoices(): string[] {
    return this.sourceDecision
      ? (this.contract(this.sourceDecision)?.decisionEvidence?.outcomeCodes ??
          [])
      : [];
  }
  get requiresReviewRequest(): boolean {
    return this.sourceDecision
      ? this.contract(
          this.sourceDecision,
        )?.decisionEvidence?.requiredCommands?.some(
          (c) => c.key.code === 'workflow.review.request',
        ) === true
      : false;
  }
  get formats(): { value: string; label: string }[] {
    return (
      this.availableProviders
        .find((p) => p.provider === completionProviders.document)
        ?.settings.find((f) => f.name === 'allowedFormats')?.choices ?? []
    );
  }
  get canAddForm(): boolean {
    return (
      !this.commands.some(
        (c) => c.capability.key.code === 'workflow.form.save',
      ) && !!this.installedCommand('workflow.form.save')
    );
  }
  get canAddDecision(): boolean {
    return (
      !this.commands.some(
        (c) => c.capability.key.code === 'workflow.review.record',
      ) && !!this.installedCommand('workflow.review.record')
    );
  }
  quorumChoices(field: string): { value: string; label: string }[] {
    return (
      this.availableProviders
        .find(
          (p) => p.provider === completionProviders.quorum && p.version === 1,
        )
        ?.settings.find((setting) => setting.name === field)?.choices ?? []
    );
  }
  private quorumMetadataSupported(
    provider: WorkflowRequirementProvider,
  ): boolean {
    const fields: Record<string, string> = {
      sourceCommand: 'capability',
      sourceConfiguration: 'string',
      ruleCode: 'string',
      ruleVersion: 'string',
      quorumThreshold: 'string',
      approvalRuleCode: 'string',
      denominatorCode: 'string',
      abstentionQuorumCode: 'string',
      tieCode: 'string',
      outcomeCodes: 'string-list',
    };
    if (
      provider.settings.length !== Object.keys(fields).length ||
      Object.entries(fields).some(
        ([name, type]) =>
          provider.settings.filter(
            (f) => f.name === name && f.type === type && f.required,
          ).length !== 1,
      )
    )
      return false;
    const expected: Record<string, string[]> = {
      ruleCode: ['platform.quorum'],
      ruleVersion: ['1'],
      approvalRuleCode: ['strict_majority', 'unanimity'],
      denominatorCode: ['eligible_members', 'votes_cast'],
      abstentionQuorumCode: ['count', 'exclude'],
      tieCode: ['no_decision', 'reject'],
      outcomeCodes: ['approved', 'rejected', 'no_decision', 'quorum_not_met'],
    };
    if (
      Object.entries(expected).some(([name, values]) => {
        const choices =
          provider.settings.find((f) => f.name === name)?.choices ?? [];
        return (
          choices.length !== values.length ||
          values.some(
            (value) => choices.filter((c) => c.value === value).length !== 1,
          )
        );
      })
    )
      return false;
    const sources =
      provider.settings.find((f) => f.name === 'sourceConfiguration')
        ?.choices ?? [];
    const action = this.installedCommand('workflow.quorum.evaluate');
    return (
      sources.length > 0 &&
      new Set(sources.map((s) => s.value)).size === sources.length &&
      sources.every(
        (s) => !!s.value && s.value.length <= 1000 && !!s.label.trim(),
      ) &&
      action?.completesStep === false
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const context = JSON.stringify([
      this.document?.workflowCode,
      this.nodeId,
      this.resetVersion,
    ]);
    if (context !== this.context) {
      this.context = context;
      this.resetPendingChanges();
      this.selectedRequirementId = null;
      this.selectionKey = this.document && this.nodeId ? this.ownerViews?.key(this.document, this.nodeId, 'requirements') ?? null : null;
      const retained = this.selectionKey ? this.ownerViews?.read<{ id: string; provider: string; version: number; settingsVersion: number; settingsHash?: string | null }>(this.selectionKey) : null;
      if (retained && this.rows.filter(row => row?.id === retained.id && row.provider === retained.provider && row.version === retained.version &&
        row.settingsVersion === retained.settingsVersion && row.settingsHash === retained.settingsHash).length === 1)
        this.selectedRequirementId = retained.id;
    } else if (!this.dirty && (changes['document'] || changes['ownerContext'] || !this.inputReference)) {
      this.inputOwner = this.ownerKey(); this.inputReference = this.documentReference(this.document);
    }
    const catalogContext = JSON.stringify([this.document?.workflowCode, this.document?.packReference, this.nodeId]);
    if (catalogContext !== this.catalogContext) {
      this.catalogContext = catalogContext;
      if (this.componentCatalog === undefined && this.document) void this.load();
    }
    if (this.componentCatalog !== undefined) {
      this.catalog = this.componentCatalog;
      this.capabilities = this.componentCapabilities;
    }
    if (changes['document'] && !this.document) this.resetPendingChanges();
    this.reconcileSelection();
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.request++;
    this.focusRequest++;
    this.pickerFocusRelease?.(); this.pickerFocusRelease = null;
  }
  async load(): Promise<void> {
    if (!this.document) return;
    const request = ++this.request;
    this.loading = true;
    this.error = '';
    this.catalogError = '';
    try {
      const [catalog, capabilities] = await Promise.all([
        firstValueFrom(this.service.getStepAuthoringCatalog(this.document)),
        firstValueFrom(this.service.getCapabilities(this.document)),
      ]);
      if (request !== this.request) return;
      this.catalog = catalog;
      this.capabilities = capabilities;
    } catch {
      if (request === this.request)
        this.error = this.catalogError =
          'Requirement types could not be loaded. Retry to see installed choices.';
    } finally {
      if (request === this.request) { this.loading = false; this.reconcileSelection(); }
    }
  }
  private contract(command: StepCommand): StepAuthoringCommand | undefined {
    const found =
      this.catalog?.commands.filter((c) =>
        sameCapabilityReference(c.capability, command.capability),
      ) ?? [];
    const pins =
      this.document?.capabilityReferences?.filter((p) =>
        sameCapabilityReference(p, command.capability),
      ) ?? [];
    return found.length === 1 && pins.length === 1 ? found[0] : undefined;
  }
  private installedCommand(code: string): StepAuthoringCommand | undefined {
    const found =
      // This existing source-creation flow deliberately owns Record v1. A newer
      // installed transport must not make it ambiguous or silently upgrade it.
      this.catalog?.commands.filter((c) => c.capability.key.code === code
        && (code !== 'workflow.review.record' || c.capability.key.version === 1)) ??
      [];
    if (found.length !== 1) return undefined;
    try {
      const installed = this.capabilities.filter(
        (c) =>
          c.key.code === code &&
          sameCapabilityReference(installedReference(c), found[0].capability),
      );
      return installed.length === 1 ? found[0] : undefined;
    } catch {
      return undefined;
    }
  }
  label(row: WorkflowCompletionRequirement): string {
    if (row?.provider === completionProviders.sdmf) return 'Complete SDMF';
    if (row?.provider === completionProviders.schedulingRequested)
      return 'Scheduling requested';
    if (row?.provider === completionProviders.hearingScheduled)
      return 'Hearing scheduled';
    if (row?.provider === completionProviders.notices)
      return 'Complete notice work';
    if (row?.provider === completionProviders.document)
      return String(row.settings?.['documentTypeLabel'] ?? 'Document supplied');
    if (row?.provider === completionProviders.form) {
      const command = this.commands.find(
        (c) => c.captureForm?.id === row.settings?.['formId'],
      );
      return command?.captureForm?.title || 'Form completed';
    }
    if (row?.provider === completionProviders.decision)
      return row.settings?.['decisionKind'] === 'review'
        ? 'Review recorded'
        : 'Decision recorded';
    if (row?.provider === completionProviders.quorum)
      return this.node?.declaration?.detail.blocks.some(isCombinedVotingRound)
        ? 'Vote and quorum result recorded'
        : 'Quorum evaluated';
    if (row?.provider === VOTING_POPULATION)
      return this.node?.declaration?.detail.blocks.some(
        isHearingPanelPopulation,
      )
        ? 'Panel confirmed'
        : 'Voting population confirmed';
    if (row?.provider === VOTING_ROUND) return 'Voting completed';
    if (row?.provider === completionProviders.hearingDecision)
      return 'Hearing decision recorded';
    if (row?.provider === completionProviders.hearingConduct)
      return 'Hearing outcome recorded';
    if (row?.provider === completionProviders.hearingGrantReview)
      return row.version === 2
        ? 'Legal review decision recorded'
        : 'Grant review approved';
    return 'Unavailable requirement';
  }
  typeLabel(provider: string): string {
    switch (provider) {
      case completionProviders.sdmf:
        return 'SDMF';
      case completionProviders.schedulingRequested:
      case completionProviders.hearingScheduled:
        return 'Hearing scheduling';
      case completionProviders.hearingDecision:
      case completionProviders.hearingConduct:
      case completionProviders.hearingGrantReview:
        return 'Hearing decision';
      case VOTING_POPULATION:
        return 'Voting population';
      case VOTING_ROUND:
        return 'Voting';
      case completionProviders.notices:
        return 'Hearing notices';
      case completionProviders.form:
        return 'Form';
      case completionProviders.document:
        return 'Document';
      case completionProviders.decision:
        return 'Decision';
      case completionProviders.quorum:
        return 'Quorum';
      default:
        return 'Saved requirement';
    }
  }
  typeIcon(provider: string): string {
    switch (provider) {
      case completionProviders.sdmf:
        return 'pi-check-square';
      case completionProviders.schedulingRequested:
      case completionProviders.hearingScheduled:
        return 'pi-calendar';
      case completionProviders.hearingDecision:
      case completionProviders.hearingConduct:
      case completionProviders.hearingGrantReview:
        return 'pi-check-square';
      case VOTING_POPULATION:
        return 'pi-users';
      case VOTING_ROUND:
        return 'pi-check-square';
      case completionProviders.notices:
        return 'pi-envelope';
      case completionProviders.form:
        return 'pi-list';
      case completionProviders.document:
        return 'pi-file';
      case completionProviders.decision:
        return 'pi-users';
      case completionProviders.quorum:
        return 'pi-chart-bar';
      default:
        return 'pi-exclamation-circle';
    }
  }
  detail(row: WorkflowCompletionRequirement): string {
    if (!this.supported(row))
      return 'Saved configuration is unavailable or ambiguous. It is preserved for inspection.';
    if (row.provider === completionProviders.sdmf)
      return 'Complete all 13 rated factors and a summary, then certify or explicitly confirm the SDMF for this visit. Uses the SDMF block’s hearing source.';
    if (row.provider === completionProviders.schedulingRequested)
      return 'A scheduling request must be active for the hearing linked to this workflow. A request does not book a date.';
    if (row.provider === completionProviders.hearingScheduled)
      return 'The linked hearing must have a current booking within its scheduling dates before staff complete this step.';
    if (row.provider === completionProviders.hearingDecision)
      return 'Finalize the linked hearing decision using the confirmed panel, evaluated voting round and certified preparation SDMF.';
    if (row.provider === completionProviders.hearingConduct)
      return 'Record the panel-supported grant or denial with the certified SDMF. Inconclusive votes return for further panel work.';
    if (row.provider === completionProviders.hearingGrantReview)
      return row.version === 2
        ? 'Record an affirmative or negative legal review decision and notes for the exact finalized grant.'
        : 'Approve the legal review attached to the exact finalized grant from the decision step configured in this page’s Grant review block.';
    if (this.isVoting(row)) {
      const source = this.document?.nodes.find(
        (n) => n.id === row.settings['sourceNodeId'],
      );
      return `Uses ${source?.displayName || source?.stepCode || 'the configured voting action'} and current voting evidence. No independent review is required.`;
    }
    if (row.provider === completionProviders.notices)
      return noticePolicyDescription(row.settings['policyJson']);
    if (row.provider === completionProviders.form)
      return 'All required answers must be saved.';
    if (row.provider === completionProviders.document)
      return `Upload a new ${((row.settings['allowedFormats'] as string[]) ?? []).join(', ').toUpperCase()} file for this visit.`;
    if (row.provider === completionProviders.quorum) {
      const source = this.quorumChoices('sourceConfiguration').find(
        (choice) => choice.value === row.settings['sourceConfiguration'],
      );
      const outcomes = this.quorumChoices('outcomeCodes').filter((choice) =>
        (row.settings['outcomeCodes'] as string[]).includes(choice.value),
      );
      return `Evaluate ${source?.label ?? 'the configured voting source'} with ${row.settings['quorumThreshold']} required participants. Completion accepts ${outcomes.map((choice) => choice.label.toLowerCase()).join(' or ')}. This does not finalize a signed decision.`;
    }
    const reference = row.settings[
      'sourceCommand'
    ] as StepCommand['capability'];
    const source = this.decisionSources.find((c) =>
      sameCapabilityReference(c.capability, reference),
    );
    const contract = source ? this.contract(source) : undefined;
    const request = contract?.decisionEvidence?.requiredCommands?.some(
      (c) => c.key.code === 'workflow.review.request',
    );
    return `${request ? 'A different person records' : `${contract?.label ?? 'Configured action'} records`} ${((row.settings['outcomeCodes'] as string[]) ?? []).map((c) => c.replaceAll('_', ' ')).join(' or ')} for this visit.`;
  }
  supported(row: WorkflowCompletionRequirement): boolean {
    return (
      !!row?.id &&
      this.rows.filter((r) => r?.id === row.id).length === 1 &&
      !!this.availableProviders.find(
        (p) =>
          p.provider === row.provider &&
          p.version === row.version &&
          p.settingsVersion === row.settingsVersion,
      ) &&
      !this.validation(row, true)
    );
  }
  overviewLabel(row: WorkflowCompletionRequirement): string {
    if (this.loading || this.catalogError || !this.supported(row))
      return `${row?.provider || 'Saved requirement'} v${row?.version ?? '?'} · retained requirement`;
    if (row.provider === completionProviders.form) {
      const sources = this.formSources.filter(command => command.captureForm?.id === row.settings['formId']);
      return `${sources[0].captureForm?.title || 'Form'} saved`;
    }
    return this.label(row);
  }
  overviewDescription(row: WorkflowCompletionRequirement): string {
    if (this.loading || this.catalogError || !this.supported(row))
      return 'The saved contract, settings or source are unavailable or ambiguous. This requirement is retained unchanged.';
    return row.provider === completionProviders.form
      ? 'Save all visible required answers for this visit.' : 'Authored completion requirement for this step.';
  }
  private fingerprint(provider = this.draft?.provider, document = this.document): string {
    const nodes = document?.nodes.filter(node => node.id === this.nodeId && node.typeCode === 'human_step') ?? [];
    const node = nodes.length === 1 ? nodes[0] : null;
    if (
      [
        VOTING_POPULATION,
        VOTING_ROUND,
        completionProviders.quorum,
        completionProviders.hearingDecision,
        completionProviders.hearingConduct,
        completionProviders.hearingGrantReview,
        completionProviders.sdmf,
        completionProviders.schedulingRequested,
        completionProviders.hearingScheduled,
      ].includes(provider || '')
    )
      return JSON.stringify({
        nodes: document?.nodes,
        fields: document?.processData?.fields,
        edges: document?.edges,
        subjectTypeCode: document?.subjectTypeCode,
        references: document?.capabilityReferences,
      });
    const behavior = node?.declaration?.behavior ?? null;
    return JSON.stringify(
      provider === completionProviders.notices
        ? {
            behavior,
            subjectTypeCode: document?.subjectTypeCode,
            fields: document?.processData?.fields ?? [],
            sources:
              node?.declaration?.detail.blocks.filter((block) =>
                domainBlockCodes.has(block.code),
              ) ?? [],
          }
        : behavior,
    );
  }
  startAdd(): void {
    if (!this.active || this.overview || !this.adoptLatestForGesture()) return;
    if (
      this.disabled ||
      !this.completionAvailable ||
      this.ambiguous ||
      this.legacyCompletingActions ||
      !this.applyPendingChanges(this.document!)
    ) {
      this.focusPendingChanges();
      return;
    }
    this.rememberFocus();
    this.draft = null;
    this.adding = true;
    this.error = '';
    this.pickerSnapshot = this.libraryContext();
    this.pickerDestination = null;
  }
  libraryOpened(): void {
    if (this.adding && this.active && !this.destroyed && !this.pickerFocusRelease)
      this.pickerFocusRelease = this.ownerViews?.suspendFocus() ?? null;
  }
  chooseLibraryProvider(id: string): void {
    if (!this.adding || !this.active || this.pickerSnapshot !== this.libraryContext() || !this.adoptLatestForGesture()) return;
    const providers = this.availableProviders.filter(provider => this.providerIdentity(provider) === id);
    if (providers.length !== 1 || this.providerIssue(providers[0]) || this.disabled) return;
    this.pickerDestination = 'detail';
    this.chooseProvider(providers[0]);
    if (!this.draft || this.adding) this.pickerDestination = null;
  }
  requestLibraryOwner(event: { id: string; actionId: string }): void {
    if (!this.adding || !this.active || this.pickerSnapshot !== this.libraryContext() || !this.adoptLatestForGesture()) return;
    const entries = this.libraryEntries.filter(entry => entry.id === event.id && entry.ownerAction?.id === event.actionId);
    if (entries.length !== 1 || event.actionId !== 'staff-page' || this.disabled) return;
    this.pickerDestination = 'staff'; this.adding = false;
  }
  libraryClosed(reason: WorkflowLibraryClosedReason): void {
    this.pickerFocusRelease?.(); this.pickerFocusRelease = null;
    const destination = this.pickerDestination;
    this.pickerDestination = null; this.adding = false;
    if (this.destroyed) return;
    if (reason === 'input' && destination && this.active && this.pickerSnapshot === this.libraryContext() && this.adoptLatestForGesture()) {
      if (destination === 'staff') this.openStaffPage.emit();
      else this.focusAfterRender('.requirement-detail-heading');
    } else if (reason === 'context-changed') {
      this.error = 'The workflow, step or installed requirement choices changed. Your existing input is retained; reopen Add requirement to inspect current choices.';
      this.focusAfterRender('.configuration-warning[role="alert"]');
    }
    this.reconcileSelection();
  }
  chooseProvider(provider: WorkflowRequirementProvider): void {
    if (!this.adoptLatestForGesture()) return;
    if (
      this.disabled ||
      !this.availableProviders.includes(provider) ||
      !!this.providerIssue(provider) ||
      !this.applyPendingChanges(this.document!)
    )
      return;
    const id = `requirement-${crypto.randomUUID()}`;
    this.original = this.fingerprint(provider.provider);
    this.draftReference = this.documentReference(this.document);
    this.originalId = null;
    this.draft = {
      id,
      provider: provider.provider,
      version: provider.version,
      settingsVersion: provider.settingsVersion,
      settingsHash: provider.settingsHash,
      settings: {},
    };
    if (provider.provider === completionProviders.sdmf) {
      const blocks =
        this.node?.declaration?.detail.blocks.filter(
          (b) =>
            b.provider === 'sdmf' &&
            b.code === 'sdmf.analysis' &&
            b.version === provider.version,
        ) ?? [];
      if (blocks.length === 1)
        this.draft.settings = {
          ...blocks[0].settings,
          completionPolicy: 'sdmf.completion.v1',
        };
    }
    if (provider.provider === completionProviders.hearingGrantReview)
      this.draft.settings = {
        ...this.node?.declaration?.detail.blocks.find(
          (b) => b.provider === 'hearing' && b.code === 'hearing.grant-review',
        )?.settings,
      };
    if (provider.provider === completionProviders.document)
      this.draft.settings = {
        documentTypeCode: id,
        documentTypeLabel: '',
        sourceMode: 'new_visit',
        allowedFormats: this.formats.map((f) => f.value),
      };
    if (provider.provider === completionProviders.notices) {
      const existing = this.node?.declaration?.detail.blocks.find((b) =>
        noticeBlockCodes.has(b.code),
      );
      this.draft.settings = {
        noticeGroupId:
          existing?.settings['noticeGroupId'] ??
          `notice-${crypto.randomUUID()}`,
        policyJson:
          existing?.settings['policyJson'] ??
          JSON.stringify(defaultNoticePolicy()),
      };
      this.noticeSourceField = '';
      this.pendingNoticeReference = null;
      this.noticeReferenceError = '';
      this.prepareNoticeHearing();
    }
    if (
      provider.provider === completionProviders.quorum &&
      provider.version === 1
    ) {
      this.draft.settings = {
        sourceCommand: structuredClone(
          this.installedCommand('workflow.quorum.evaluate')!.capability,
        ),
        sourceConfiguration: '',
        ruleCode: 'platform.quorum',
        ruleVersion: '1',
        quorumThreshold: '',
        approvalRuleCode: '',
        denominatorCode: '',
        abstentionQuorumCode: '',
        tieCode: '',
        outcomeCodes: [],
      };
    }
    if (isVotingRequirement(provider.provider, provider.version))
      this.draft.settings = {
        sourceNodeId: '',
        allMembers: false,
        ...(provider.provider === completionProviders.quorum
          ? { outcomeCodes: [] }
          : {}),
      };
    this.adding = false;
    this.changed();
    if (!this.pickerFocusRelease) this.focusAfterRender('.requirement-detail-heading');
  }
  select(row: WorkflowCompletionRequirement): void {
    if (!this.active || this.overview || !this.adoptLatestForGesture()) return;
    const matches = this.rows.filter(current => !!row?.id && current?.id === row.id && JSON.stringify(current) === JSON.stringify(row));
    if (matches.length !== 1 || this.adding) return;
    if (
      this.loading || this.interactionBlocked ||
      !this.applyPendingChanges(this.document!)
    ) {
      this.focusPendingChanges();
      return;
    }
    this.rememberFocus();
    row = this.rows.find(current => current.id === row.id)!;
    this.selectedRequirementId = row.id; this.rememberSelection();
    if (!this.canAuthor || !this.supported(row)) { this.draft = null; this.focusAfterRender('.requirement-detail-heading'); return; }
    this.original = this.fingerprint(row.provider);
    this.draftReference = this.documentReference(this.document);
    this.originalId = row.id;
    this.draft = structuredClone(row);
    this.noticeSourceField = '';
    this.pendingNoticeReference = null;
    this.noticeReferenceError = '';
    this.error = '';
    this.focusAfterRender('.requirement-detail-heading');
  }
  changed(): void {
    this.dirty = true;
    this.pendingChangesChange.emit(true);
    this.error = '';
  }
  chooseForm(id: string): void {
    if (this.draft) {
      this.draft.settings['formId'] = id;
      this.changed();
    }
  }
  chooseDecision(id: string): void {
    const command = this.decisionSources.find((c) => commandIdentity(c) === id);
    if (!this.draft || !command) return;
    const evidence = this.contract(command)!.decisionEvidence!;
    this.draft.settings = {
      sourceCommand: structuredClone(command.capability),
      decisionKind: evidence.decisionKind,
      outcomeCodes: [...evidence.outcomeCodes],
    };
    this.changed();
  }
  setQuorumThreshold(value: number | null): void {
    if (!this.draft || this.disabled) return;
    this.draft.settings['quorumThreshold'] =
      value === null ? '' : String(value);
    this.changed();
  }
  decisionIdentity(): string {
    return this.sourceDecision ? commandIdentity(this.sourceDecision) : '';
  }
  checked(field: string, value: string): boolean {
    return (
      Array.isArray(this.draft?.settings[field]) &&
      (this.draft!.settings[field] as string[]).includes(value)
    );
  }
  toggle(field: string, value: string, checked: boolean): void {
    if (!this.draft) return;
    const values = (this.draft.settings[field] as string[] | undefined) ?? [];
    this.draft.settings[field] = checked
      ? [...new Set([...values, value])]
      : values.filter((v) => v !== value);
    this.changed();
  }
  private validation(
    row: WorkflowCompletionRequirement,
    requireDeclaredSource = false,
  ): string {
    if (
      !this.availableProviders.some(
        (p) =>
          p.provider === row.provider &&
          p.version === row.version &&
          p.settingsVersion === row.settingsVersion &&
          p.settingsHash === row.settingsHash,
      )
    )
      return 'The installed requirement contract has changed or is unavailable. Its saved settings are preserved.';
    const settings = row.settings;
    if (!settings || typeof settings !== 'object' || Array.isArray(settings))
      return 'The saved settings cannot be edited safely.';
    if (row.provider === completionProviders.sdmf) return this.sdmfIssue(row);
    if (
      row.provider === completionProviders.schedulingRequested ||
      row.provider === completionProviders.hearingScheduled
    ) {
      if (
        Object.keys(settings).length ||
        this.document?.subjectTypeCode !== 'hearing_cycle'
      )
        return 'Scheduling requirements use the hearing linked to this lifecycle workflow.';
      return this.schedulingIssue();
    }
    if (row.provider === completionProviders.hearingGrantReview) {
      const block = this.node?.declaration?.detail.blocks.find(
        (b) => b.provider === 'hearing' && b.code === 'hearing.grant-review',
      );
      const source = this.document?.nodes.find(
        (n) => n.id === settings['sourceNodeId'],
      );
      return Object.keys(settings).length === 1 &&
        typeof settings['sourceNodeId'] === 'string' &&
        block?.settings['sourceNodeId'] === settings['sourceNodeId'] &&
        this.document?.subjectTypeCode === 'hearing_cycle' &&
        source?.declaration?.detail.blocks.some(
          (b) =>
            b.provider === 'hearing' &&
            b.code === 'hearing.decision' &&
            b.version === 2,
        )
        ? ''
        : 'Configure the Grant review block with its Conduct hearing step first.';
    }
    if (
      row.provider === completionProviders.hearingDecision ||
      row.provider === completionProviders.hearingConduct
    ) {
      if (Object.keys(settings).length)
        return 'The hearing decision requirement uses its page block configuration.';
      if (
        row.provider === completionProviders.hearingConduct &&
        !this.node?.declaration?.detail.blocks.some(
          (b) =>
            b.provider === 'hearing' &&
            b.code === 'hearing.decision' &&
            b.version === 2 &&
            b.settings['quorumNodeId'] === this.nodeId &&
            b.settings['voteMeaning'] === 'grant-proposal',
        )
      )
        return 'Add the current Hearing decision and Voting result blocks to Conduct hearing.';
      return this.document?.subjectTypeCode === 'hearing_cycle' &&
        this.node?.declaration?.detail.blocks.some(
          (b) => b.provider === 'hearing' && b.code === 'hearing.decision',
        ) &&
        this.commands.some(
          (c) =>
            c.capability.key.provider === 'hearing' &&
            c.capability.key.code === 'hearing.finalize-decision',
        )
        ? ''
        : 'Add the Hearing decision block on this step’s staff page first.';
    }
    if (this.isVoting(row)) {
      if (!requireDeclaredSource && row === this.draft && this.votingEditor)
        return this.votingEditor.issue();
      const source = this.document?.nodes.find(
        (n) => n.id === settings['sourceNodeId'],
      );
      if (
        !source ||
        !source.declaration?.detail.blocks.some((b) =>
          providesVotingBlock(
            b,
            row.provider === VOTING_POPULATION
              ? 'voting.population'
              : 'voting.ballots',
          ),
        )
      )
        return requireDeclaredSource
          ? 'The configured population or voting action is unavailable.'
          : '';
      if (
        row.provider === completionProviders.quorum &&
        (!Array.isArray(settings['outcomeCodes']) ||
          !settings['outcomeCodes'].length)
      )
        return 'Choose the quorum outcomes that permit completion.';
      return '';
    }
    if (row.provider === completionProviders.notices) {
      const issue = noticeRequirementIssue(settings);
      if (issue) return issue;
      const blocks =
        this.node?.declaration?.detail.blocks.filter((b) =>
          noticeBlockCodes.has(b.code),
        ) ?? [];
      if (
        blocks.some(
          (b) => b.settings['noticeGroupId'] !== settings['noticeGroupId'],
        )
      )
        return 'Reuse the existing notice group for this step.';
      if (
        requireDeclaredSource &&
        (!blocks.some((b) => b.code === 'notices.delivery') ||
          blocks.some(
            (b) => b.settings['policyJson'] !== settings['policyJson'],
          ))
      )
        return 'The notice group and requirement policy must agree.';
      if (
        this.rows.some(
          (other) =>
            other.id !== row.id &&
            other.provider === completionProviders.notices,
        )
      )
        return 'This step supports one notice completion requirement.';
      return '';
    }
    const allowed =
      row.provider === completionProviders.form
        ? ['formId']
        : row.provider === completionProviders.document
          ? [
              'documentTypeCode',
              'documentTypeLabel',
              'sourceMode',
              'allowedFormats',
            ]
          : row.provider === completionProviders.decision
            ? ['sourceCommand', 'decisionKind', 'outcomeCodes']
            : row.provider === completionProviders.quorum
              ? [
                  'sourceCommand',
                  'sourceConfiguration',
                  'ruleCode',
                  'ruleVersion',
                  'quorumThreshold',
                  'approvalRuleCode',
                  'denominatorCode',
                  'abstentionQuorumCode',
                  'tieCode',
                  'outcomeCodes',
                ]
              : [];
    if (Object.keys(settings).some((k) => !allowed.includes(k)))
      return 'Additional saved settings are preserved. Inspect this requirement before replacing it.';
    if (row.provider === completionProviders.form)
      return this.formSources.filter(
        (c) => c.captureForm?.id === settings['formId'],
      ).length === 1
        ? ''
        : 'Choose one configured save-only form.';
    if (row.provider === completionProviders.document) {
      if (
        typeof settings['documentTypeLabel'] !== 'string' ||
        !settings['documentTypeLabel'].trim() ||
        settings['documentTypeLabel'].length > 120
      )
        return 'Enter a document name (up to 120 characters).';
      if (
        typeof settings['documentTypeCode'] !== 'string' ||
        settings['documentTypeCode'].length > 120 ||
        !/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/.test(
          settings['documentTypeCode'],
        ) ||
        settings['sourceMode'] !== 'new_visit'
      )
        return 'This document source is unavailable.';
      return Array.isArray(settings['allowedFormats']) &&
        settings['allowedFormats'].length > 0 &&
        new Set(settings['allowedFormats']).size ===
          settings['allowedFormats'].length &&
        settings['allowedFormats'].every((v) =>
          this.formats.some((f) => f.value === v),
        )
        ? ''
        : 'Choose at least one supported file format.';
    }
    if (row.provider === completionProviders.decision) {
      const ref = settings['sourceCommand'] as
        | StepCommand['capability']
        | undefined;
      const source = ref?.key
        ? this.decisionSources.find((c) =>
            sameCapabilityReference(c.capability, ref),
          )
        : undefined;
      const evidence = source ? this.contract(source)?.decisionEvidence : null;
      return evidence &&
        settings['decisionKind'] === evidence.decisionKind &&
        Array.isArray(settings['outcomeCodes']) &&
        settings['outcomeCodes'].length > 0 &&
        new Set(settings['outcomeCodes']).size ===
          settings['outcomeCodes'].length &&
        settings['outcomeCodes'].every((o) =>
          evidence.outcomeCodes.includes(o),
        ) &&
        (evidence.allowOutcomeSubset === true ||
          settings['outcomeCodes'].length === evidence.outcomeCodes.length)
        ? ''
        : 'Choose a recorded decision action and its supported results.';
    }
    if (row.provider === completionProviders.quorum) {
      const action = this.installedCommand('workflow.quorum.evaluate');
      const reference = settings['sourceCommand'] as
        | StepCommand['capability']
        | undefined;
      if (
        !action ||
        action.completesStep !== false ||
        !reference?.key ||
        !sameCapabilityReference(reference, action.capability) ||
        (requireDeclaredSource &&
          this.commands.filter(
            (command) =>
              sameCapabilityReference(command.capability, reference) &&
              !!this.contract(command),
          ).length !== 1)
      )
        return 'The exact installed quorum evaluation action is unavailable.';
      if (
        settings['ruleCode'] !== 'platform.quorum' ||
        settings['ruleVersion'] !== '1'
      )
        return 'The saved quorum rule version is unavailable.';
      if (
        this.quorumChoices('sourceConfiguration').filter(
          (choice) => choice.value === settings['sourceConfiguration'],
        ).length !== 1
      )
        return 'Choose one installed voting source.';
      if (
        typeof settings['quorumThreshold'] !== 'string' ||
        !/^[1-9][0-9]*$/.test(settings['quorumThreshold']) ||
        Number(settings['quorumThreshold']) > 256
      )
        return 'Enter a whole-number quorum threshold from 1 to 256.';
      for (const field of [
        'approvalRuleCode',
        'denominatorCode',
        'abstentionQuorumCode',
        'tieCode',
      ]) {
        if (
          this.quorumChoices(field).filter(
            (choice) => choice.value === settings[field],
          ).length !== 1
        )
          return 'Choose an approval rule, denominator, abstention rule and tie rule.';
      }
      return Array.isArray(settings['outcomeCodes']) &&
        settings['outcomeCodes'].length > 0 &&
        new Set(settings['outcomeCodes']).size ===
          settings['outcomeCodes'].length &&
        settings['outcomeCodes'].every((outcome) =>
          this.quorumChoices('outcomeCodes').some(
            (choice) => choice.value === outcome,
          ),
        )
        ? ''
        : 'Choose at least one calculation result that permits completion.';
    }
    return 'This requirement provider is unavailable.';
  }
  private sdmfIssue(row: WorkflowCompletionRequirement): string {
    const blocks =
      this.node?.declaration?.detail.blocks.filter(
        (b) => b.provider === 'sdmf' && b.code === 'sdmf.analysis',
      ) ?? [];
    const block = blocks[0],
      settings = row.settings;
    if (
      blocks.length !== 1 ||
      block.version !== row.version ||
      block.settingsVersion !== row.settingsVersion ||
      block.region !== 'main' ||
      block.width !== 'full'
    )
      return 'Add one matching full-width SDMF analysis block to this step’s staff page first.';
    if (
      Object.keys(settings).some(
        (key) =>
          !['bindingMode', 'referenceField', 'completionPolicy'].includes(key),
      ) ||
      settings['completionPolicy'] !== 'sdmf.completion.v1' ||
      !['linked-subject', 'process-reference'].includes(
        String(settings['bindingMode']),
      ) ||
      settings['bindingMode'] !== block.settings['bindingMode'] ||
      (settings['referenceField'] ?? null) !==
        (block.settings['referenceField'] ?? null) ||
      (settings['bindingMode'] === 'linked-subject'
        ? settings['referenceField'] != null
        : typeof settings['referenceField'] !== 'string' ||
          !settings['referenceField'].trim())
    )
      return 'The requirement must use the SDMF block’s hearing source and installed completion policy.';
    const manifest = domainBlockManifests.find(
      (m) =>
        m.provider === 'sdmf' &&
        m.code === block.code &&
        m.version === block.version,
    );
    const readsAvailable = manifest?.reads.every((read) => {
      const entries = this.capabilities.filter(
        (c) =>
          c.key.provider === 'sdmf' &&
          c.key.kind === 'view' &&
          c.key.code === read.code &&
          c.key.version === read.version,
      );
      return (
        entries.length === 1 &&
        this.document?.capabilityReferences?.filter((p) =>
          sameCapabilityReference(p, installedReference(entries[0])),
        ).length === 1
      );
    });
    if (
      !readsAvailable ||
      [...sdmfCommandCodes].some((code) => {
        const commands = this.commands.filter(
          (c) =>
            c.capability.key.provider === 'sdmf' &&
            c.capability.key.code === code &&
            c.capability.key.version === row.version,
        );
        return (
          commands.length !== 1 ||
          this.contract(commands[0])?.completesStep !== false
        );
      })
    )
      return 'The SDMF block’s installed actions or readers are unavailable. Restore the block’s exact dependencies before editing this requirement.';
    if (
      this.rows.some(
        (other) =>
          other.id !== row.id && other.provider === completionProviders.sdmf,
      )
    )
      return 'This step supports one SDMF completion requirement.';
    return '';
  }
  private schedulingIssue(): string {
    const document = this.document!;
    const producers = new Set(
      document.nodes
        .filter((node) => {
          const automatic = node.automation?.command;
          return (
            (automatic?.key.provider === 'scheduling' &&
              automatic.key.code === 'scheduling.request-automated' &&
              automatic.key.version === 1) ||
            node.declaration?.behavior.commands.some(
              (c) =>
                c.capability.key.provider === 'scheduling' &&
                c.capability.key.code === 'scheduling.request' &&
                c.capability.key.version === 1 &&
                !!this.catalog?.commands.some((installed) =>
                  sameCapabilityReference(installed.capability, c.capability),
                ),
            )
          );
        })
        .map((node) => node.id),
    );
    const pending = document.nodes
        .filter((node) => node.typeCode === 'entry')
        .map((node) => node.id),
      seen = new Set<string>();
    while (pending.length) {
      const id = pending.pop()!;
      if (producers.has(id) || seen.has(id)) continue;
      if (id === this.nodeId)
        return 'Add Request scheduling on every path before this requirement.';
      seen.add(id);
      pending.push(
        ...document.edges
          .filter(
            (edge) => edge.fromNodeId === id && edge.pathKindCode !== 'return',
          )
          .map((edge) => edge.toNodeId),
      );
    }
    return '';
  }
  applyPendingChanges(document: WorkflowDefinitionDocument): boolean {
    if (!this.dirty) return true;
    if (this.ownerContext && this.ownerKey() !== this.inputOwner) {
      this.error = `The draft or author context changed while this requirement was open. Your input is retained. Use ${this.originalId ? 'Discard unapplied edit' : 'Discard new requirement'} to review the current settings before editing again.`;
      return false;
    }
    if (this.disabled || !this.draft || !this.knownFormat(document)) return false;
    this.document = document;
    if (this.documentReference(document) !== this.draftReference || !this.node || this.ambiguous || this.fingerprint() !== this.original) {
      this.error =
        'This step changed while the requirement was open. Cancel and reopen it to use the current configuration.';
      return false;
    }
    const row = structuredClone(this.draft);
    if (row.provider === completionProviders.document && typeof row.settings['documentTypeLabel'] === 'string')
      row.settings['documentTypeLabel'] = row.settings['documentTypeLabel'].trim();
    if (this.isVoting() && this.votingEditor)
      row.settings = this.votingEditor.requirementSettings;
    this.error = this.validation(row);
    if (this.error) return false;
    try {
      const next = this.buildPendingDocument(document, row);
      this.selectedRequirementId = row.id;
      this.document = next;
      this.inputReference = this.documentReference(next);
      this.resetPendingChanges();
      this.documentChange.emit(next);
      return true;
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The requirement could not be added.';
      return false;
    }
  }
  private buildPendingDocument(document: WorkflowDefinitionDocument, row: WorkflowCompletionRequirement): WorkflowDefinitionDocument {
      let sourceDocument = document;
      if (this.isVoting()) {
        if (!this.votingEditor)
          throw new Error(
            'Open the voting configuration before applying this requirement.',
          );
        sourceDocument = this.votingEditor.previewPendingDocument(document);
      }
      if (row.provider === completionProviders.notices) {
        if (this.noticeReferenceError)
          throw new Error(this.noticeReferenceError);
        if (this.pendingNoticeReference) {
          const issue = noticeHearingFieldIssue(
            document,
            this.pendingNoticeReference,
          );
          if (issue) throw new Error(issue);
          sourceDocument = {
            ...document,
            payloadSchemaVersion: requireWorkflowPayloadVersion(
              document.payloadSchemaVersion,
              '1.10',
            ),
            processData: {
              ...document.processData,
              fields: [
                ...(document.processData?.fields ?? []),
                structuredClone(this.pendingNoticeReference),
              ],
            },
          };
        }
      }
      let next = this.ensureComplete(
        initializeBlankStep(sourceDocument, this.nodeId!),
      );
      if (
        row.provider === completionProviders.quorum &&
        row.version === 1
      )
        next = this.ensureQuorumAction(next);
      if (row.provider === completionProviders.notices) {
        const source = resolveNoticeSource(
          next,
          this.nodeId!,
          this.noticeSourceField,
        );
        if (source.kind !== 'resolved')
          throw new Error(
            source.kind === 'blocked'
              ? source.message
              : source.kind === 'missing'
                ? 'The workflow needs a hearing connection before notice work can be applied. Cancel and reopen this requirement to configure it.'
                : 'Choose a Hearing reference for this step.',
          );
        next = ensureNoticeGroup(
          next,
          this.nodeId!,
          row.settings,
          this.capabilities,
          source.source,
        );
      }
      const node = next.nodes.find((n) => n.id === this.nodeId)!;
      const rows = node.declaration!.behavior.completionRequirements ?? [];
      const draft = structuredClone(row);
      next = {
        ...next,
        payloadSchemaVersion: requireWorkflowPayloadVersion(
          next.payloadSchemaVersion,
          '1.8',
        ),
        nodes: next.nodes.map((n) =>
          n.id !== this.nodeId
            ? n
            : {
                ...n,
                declaration: {
                  ...n.declaration!,
                  behavior: {
                    ...n.declaration!.behavior,
                    completionRequirements: this.originalId
                      ? rows.map((r) => (r.id === this.originalId ? draft : r))
                      : [...rows, draft],
                  },
                },
              },
        ),
      };
      next = applyRequirementDetailDefaults(next, this.nodeId!, this.catalog);
      return next;
  }
  private ensureComplete(
    document: WorkflowDefinitionDocument,
  ): WorkflowDefinitionDocument {
    const complete = this.installedCommand('workflow.step.complete');
    if (!complete || complete.supportsCompletionRequirements !== true)
      throw new Error(
        'The installed Complete step action is unavailable. Reload requirement types before adding requirements.',
      );
    const existing =
      document.nodes
        .find((n) => n.id === this.nodeId)
        ?.declaration?.behavior.commands.filter(
          (c) => c.capability.key.code === 'workflow.step.complete',
        ) ?? [];
    if (
      existing.length === 1 &&
      sameCapabilityReference(existing[0].capability, complete.capability)
    )
      return document;
    if (existing.length)
      throw new Error(
        'The saved Complete step action has a different contract. Inspect staff actions before continuing.',
      );
    return addStepCommand(document, this.nodeId!, complete, this.capabilities);
  }
  private ensureQuorumAction(
    document: WorkflowDefinitionDocument,
  ): WorkflowDefinitionDocument {
    const action = this.installedCommand('workflow.quorum.evaluate');
    if (!action || action.completesStep !== false)
      throw new Error(
        'The installed quorum evaluation action is unavailable. Reload requirement types.',
      );
    const existing =
      document.nodes
        .find((node) => node.id === this.nodeId)
        ?.declaration?.behavior.commands.filter(
          (command) =>
            command.capability.key.code === 'workflow.quorum.evaluate',
        ) ?? [];
    if (
      existing.length === 1 &&
      sameCapabilityReference(existing[0].capability, action.capability)
    )
      return document;
    if (existing.length)
      throw new Error(
        'The saved quorum evaluation action has a different contract. Inspect staff actions before continuing.',
      );
    return addStepCommand(document, this.nodeId!, action, this.capabilities);
  }
  hasPendingChanges(): boolean { return this.dirty; }
  private documentReference(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.payloadSchemaVersion, document?.packReference, this.nodeId, this.resetVersion]);
  }
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'requirements', label: 'completion requirement input',
      state: () => {
        if (!this.dirty) return 'clean';
        const latest = latestDocument();
        if (!latest || (this.ownerContext && this.ownerKey() !== this.inputOwner) || this.documentReference(latest) !== this.draftReference ||
          this.fingerprint(undefined, latest) !== this.original || this.fingerprint() !== this.original) return 'stale';
        if (this.disabled || !this.knownFormat(latest) || !this.draft || this.ambiguous || !this.completionAvailable) return 'invalid';
        try {
          const row = structuredClone(this.draft);
          if (row.provider === completionProviders.document && typeof row.settings['documentTypeLabel'] === 'string')
            row.settings['documentTypeLabel'] = row.settings['documentTypeLabel'].trim();
          if (this.isVoting() && (!this.votingEditor || this.votingEditor.issue())) return 'invalid';
          if (this.isVoting() && this.votingEditor) row.settings = this.votingEditor.requirementSettings;
          if (this.noticeReferenceError || (this.pendingNoticeReference && noticeHearingFieldIssue(latest, this.pendingNoticeReference))) return 'invalid';
          if (this.validation(row)) return 'invalid';
          this.buildPendingDocument(latest, row);
          return 'valid';
        } catch { return 'invalid'; }
      },
      fingerprint: () => JSON.stringify([this.dirty, this.original, this.originalId, this.draftReference,
        this.documentReference(latestDocument()), this.fingerprint(undefined, latestDocument()), this.fingerprint(),
        this.draft, this.noticeSourceField, this.pendingNoticeReference, this.noticeReferenceError,
        this.isVoting() && this.votingEditor?.pendingInputFingerprint(),
        this.catalog, this.capabilities, this.canAuthor, this.interactionBlocked, this.loading, this.catalogError, this.inputOwner, this.ownerKey()]),
      apply: () => {
        const latest = latestDocument();
        return latest ? this.applyPendingChanges(latest) : !this.dirty;
      },
      discard: () => { this.document = latestDocument(); this.resetPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  resetPendingChanges(): void {
    this.focusRequest++;
    this.returnFocus = null;
    this.draft = null;
    this.adding = false;
    this.originalId = null;
    this.original = '';
    this.draftReference = '';
    this.noticeSourceField = '';
    this.pendingNoticeReference = null;
    this.noticeReferenceError = '';
    this.error = '';
    this.dirty = false;
    this.inputOwner = this.ownerKey(); this.inputReference = this.documentReference(this.document);
    this.pendingChangesChange.emit(false);
    this.reconcileSelection();
  }
  focusPendingChanges(): void {
    const selector = this.draft?.provider === completionProviders.document &&
      (typeof this.draft.settings['documentTypeLabel'] !== 'string' || !this.draft.settings['documentTypeLabel'].trim())
      ? '#requirement-document-name' : this.draft?.provider === completionProviders.form && !this.canOpenForm
        ? '#requirement-form-source' : this.error ? '.configuration-warning[role="alert"]'
          : '.requirement-form input:not([type="checkbox"]), .requirement-form select';
    this.focusAfterRender(selector);
  }
  /** Reveal an exact saved row without discarding any unapplied input. */
  inspectRequirementCard(id: string, canFocus?: () => boolean): boolean {
    if (!id || this.destroyed || !this.active || this.overview || this.interactionBlocked || this.dirty || this.adding
      || canFocus?.() === false || !this.adoptLatestForGesture()) return false;
    if (this.rows.filter(row => row?.id === id).length !== 1) return false;
    this.selectedRequirementId = id;
    this.resetPendingChanges();
    this.rememberSelection();
    if (canFocus?.() === false) return false;
    this.focusRequirementCard(id, canFocus);
    return true;
  }
  /** Focus a retained requirement without opening a form or changing its data. */
  focusRequirementCard(id: string, canFocus?: () => boolean): void {
    if (!id || this.rows.filter((row) => row?.id === id).length !== 1) return;
    const request = ++this.focusRequest;
    const context = JSON.stringify([this.ownerKey(), this.documentReference(this.currentDocument())]);
    setTimeout(() => {
      if (this.destroyed || !this.active || this.overview || this.ownerViews?.focusSuspended() || request !== this.focusRequest || canFocus?.() === false || this.element.nativeElement.closest('[hidden],[inert]') ||
        context !== JSON.stringify([this.ownerKey(), this.documentReference(this.currentDocument())])) return;
      const cards = Array.from(
        this.element.nativeElement.querySelectorAll<HTMLElement>(
          '[data-requirement-id]',
        ),
      ).filter((card) => card.getAttribute('data-requirement-id') === id);
      if (cards.length === 1)
        (cards[0].matches('button') ? cards[0] : cards[0].querySelector<HTMLElement>('button, h4'))?.focus();
    });
  }
  cancelPendingChanges(): void {
    const origin = this.returnFocus;
    this.resetPendingChanges();
    this.restoreFocus(origin);
  }
  applyRequirement(): void {
    const origin = this.returnFocus;
    if (this.document && this.applyPendingChanges(this.document))
      this.restoreFocus(origin);
    else this.focusPendingChanges();
  }
  private rememberFocus(): void {
    const active = this.element.nativeElement.ownerDocument.activeElement;
    this.returnFocus =
      active instanceof HTMLElement &&
      this.element.nativeElement.contains(active)
        ? active
        : null;
  }
  private restoreFocus(origin: HTMLElement | null): void {
    this.focusAfterRender('.add-requirement', origin);
  }
  private focusAfterRender(
    selector: string,
    origin?: HTMLElement | null,
  ): void {
    const request = ++this.focusRequest;
    const context = JSON.stringify([this.ownerKey(), this.documentReference(this.currentDocument()), this.selectedRequirementId, this.draft?.id]);
    setTimeout(() => {
      if (this.destroyed || !this.active || this.overview || this.adding || this.ownerViews?.focusSuspended() || request !== this.focusRequest ||
        context !== JSON.stringify([this.ownerKey(), this.documentReference(this.currentDocument()), this.selectedRequirementId, this.draft?.id])) return;
      const host = this.element.nativeElement;
      if (!host.isConnected || host.closest('[hidden],[inert]')) return;
      const target =
        origin?.isConnected && host.contains(origin)
          ? origin
          : host.querySelector<HTMLElement>(selector) ?? host.querySelector<HTMLElement>('.requirement-detail-heading');
      if (target && !target.closest('[hidden],[inert]') && !target.matches(':disabled')) target.focus();
    });
  }
  remove(row: WorkflowCompletionRequirement): void {
    if (!this.adoptLatestForGesture()) return;
    if (this.rows.filter(current => !!row?.id && current?.id === row.id && JSON.stringify(current) === JSON.stringify(row)).length !== 1) return;
    if (
      this.disabled ||
      this.ambiguous ||
      !this.applyPendingChanges(this.document!)
    )
      return;
    const next = {
      ...this.document!,
      nodes: this.document!.nodes.map((n) =>
        n.id !== this.nodeId
          ? n
          : {
              ...n,
              declaration: {
                ...n.declaration!,
                behavior: {
                  ...n.declaration!.behavior,
                  completionRequirements: this.rows.filter(
                    (r) => r.id !== row.id,
                  ),
                },
              },
            },
      ),
    };
    this.document = next;
    if (this.selectedRequirementId === row.id) this.selectedRequirementId = null;
    this.resetPendingChanges();
    this.documentChange.emit(next);
    this.focusAfterRender('.requirement-list button, .add-requirement');
  }
  openForm(row: WorkflowCompletionRequirement): void {
    if (!this.adoptLatestForGesture()) return;
    if (!this.applyPendingChanges(this.document!)) return;
    row = this.rows.find(current => current.id === row.id) ?? row;
    if (!this.supported(row)) return;
    const found = this.formSources.filter(
      (c) => c.captureForm?.id === row.settings['formId'],
    );
    if (found.length === 1) this.editForm.emit(found[0]);
  }
  addSource(kind: 'form' | 'decision'): void {
    if (!this.adoptLatestForGesture()) return;
    if (
      this.disabled ||
      !this.draft ||
      this.originalId ||
      this.ambiguous ||
      this.legacyCompletingActions
    )
      return;
    if (this.documentReference(this.document) !== this.draftReference || this.fingerprint() !== this.original) {
      this.error = 'This step changed while the requirement was open. Your input is retained; discard it and reopen the requirement to inspect the latest source.';
      return;
    }
    const before = this.document!,
      previousDraft = structuredClone(this.draft),
      previousFingerprint = this.original,
      previousReference = this.draftReference,
      origin = this.returnFocus;
    try {
      const contract = this.installedCommand(
        kind === 'form' ? 'workflow.form.save' : 'workflow.review.record',
      );
      if (!contract)
        throw new Error(
          'This source action is unavailable. Reload requirement types.',
        );
      let next = this.document!;
      for (const reference of contract.decisionEvidence?.requiredCommands ??
        []) {
        const required =
          this.catalog?.commands.filter((c) =>
            sameCapabilityReference(c.capability, reference),
          ) ?? [];
        if (required.length !== 1 || required[0].completesStep !== false)
          throw new Error(
            'A required save-only source action is unavailable. Reload requirement types.',
          );
        const existing =
          next.nodes
            .find((n) => n.id === this.nodeId)
            ?.declaration?.behavior.commands.filter(
              (c) => commandIdentity(c) === commandIdentity(required[0]),
            ) ?? [];
        if (
          existing.length > 1 ||
          (existing.length === 1 &&
            !sameCapabilityReference(existing[0].capability, reference))
        )
          throw new Error(
            'A required source action has a different saved contract. Inspect staff actions.',
          );
        if (!existing.length)
          next = addStepCommand(
            next,
            this.nodeId!,
            required[0],
            this.capabilities,
          );
      }
      next = addStepCommand(next, this.nodeId!, contract, this.capabilities);
      this.document = next;
      this.original = this.fingerprint();
      this.draftReference = this.documentReference(next);
      const command = this.commands.find((c) =>
        sameCapabilityReference(c.capability, contract.capability),
      )!;
      if (kind === 'form') this.chooseForm(command.captureForm!.id);
      else this.chooseDecision(commandIdentity(command));
      // Source actions, their default requirement, and Complete are one undoable composition.
      if (!this.applyPendingChanges(next)) {
        this.document = before;
        this.draft = previousDraft;
        this.original = previousFingerprint;
        this.draftReference = previousReference;
        return;
      }
      // A new form needs authoring before it can qualify; use Detail's canonical editor.
      if (kind === 'form')
        this.editForm.emit(
          this.commands.find((c) =>
            sameCapabilityReference(c.capability, contract.capability),
          )!,
        );
      else this.restoreFocus(origin);
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The source could not be added.';
    }
  }
  commandId(command: StepCommand): string {
    return commandIdentity(command);
  }
  commandLabel(command: StepCommand): string {
    return this.contract(command)?.label ?? command.capability.key.code;
  }
}
